
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom'; // Importante para romper el contenedor
import { FileText, Download, Loader2, Eye, X, ZoomIn } from 'lucide-react';
import Swal from 'sweetalert2';
import API_BASE_URL from '../../Config';

export default function SocioDocumentos({ documentos, loading, formatDate }) {
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewTitle, setPreviewTitle] = useState('');

    useEffect(() => {
        const handleEsc = (event) => {
            if (event.keyCode === 27) closePreview();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, []);

    const handleAction = async (doc) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/socio/documentos/${doc.id}/descargar`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('No se pudo obtener el archivo');
            
            const blob = await response.blob();
            let contentType = response.headers.get('content-type') || blob.type;
            const url = window.URL.createObjectURL(blob);

            if (contentType && contentType.startsWith('image/')) {
                setPreviewUrl(url);
                setPreviewTitle(`${doc.tipoDocumento?.nombreTipo} ${doc.lado ? `- ${doc.lado}` : ''}`);
                setIsPreviewOpen(true);
            } else {
                const a = document.createElement('a');
                a.href = url;
                a.download = `documento_${doc.id}.pdf`;
                a.click();
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', 'No se pudo procesar el documento', 'error');
        }
    };

    const closePreview = () => {
        if (previewUrl) window.URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setIsPreviewOpen(false);
    };

    // Componente del Modal para usar con Portal
    const ModalPortal = ({ children }) => {
        return createPortal(
            <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md">
                {children}
            </div>,
            document.body
        );
    };

    return (
        <div className="animate-premium-in space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="w-1.5 h-8 bg-slate-900 rounded-full" />
                    <div>
                        <h2 className="text-xl font-black text-slate-800 tracking-tighter uppercase">Expediente Digital</h2>
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Legajo de documentos verificados</p>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                    <Loader2 className="w-10 h-10 animate-spin text-uni-teal mb-4" />
                    <p className="text-[10px] font-black uppercase tracking-widest">Sincronizando...</p>
                </div>
            ) : documentos.length === 0 ? (
                <div className="enterprise-card p-12 text-center border-dashed border-2 bg-slate-50/50 border-slate-200">
                    <FileText className="w-10 h-10 text-slate-300 mx-auto mb-4 opacity-50" />
                    <p className="text-slate-400 font-black text-[10px] uppercase">Sin documentos</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {documentos.map((doc, index) => (
                        <div key={index} className="border border-slate-200 bg-white p-4 rounded-xl flex items-center justify-between group hover:border-uni-teal transition-all shadow-sm">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-slate-50 rounded-lg text-slate-400 group-hover:bg-uni-teal/10 group-hover:text-uni-teal transition-all">
                                    <FileText className="w-5 h-5" />
                                </div>
                                <div>
                                    <h4 className="font-black text-slate-800 text-xs uppercase tracking-tight truncate max-w-[150px]">
                                        {doc.tipoDocumento?.nombreTipo} {doc.lado ? `- ${doc.lado}` : ''}
                                    </h4>
                                    <p className="text-[8px] text-slate-400 font-black uppercase">{formatDate(doc.fechaSubida)}</p>
                                </div>
                            </div>
                            <button 
                                onClick={() => handleAction(doc)}
                                className="w-8 h-8 flex items-center justify-center bg-slate-900 text-white rounded-lg hover:bg-uni-teal transition-all"
                            >
                                <Eye className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* MODAL CON PORTAL: Rompe cualquier contenedor y se dibuja sobre el body */}
            {isPreviewOpen && (
                <ModalPortal>
                    <div className="bg-white rounded-[2rem] shadow-[0_50px_100px_rgba(0,0,0,0.8)] w-full max-w-5xl h-auto max-h-[90vh] flex flex-col overflow-hidden border-4 border-slate-800/10">
                        
                        {/* Cabecera Técnica - Siempre visible arriba */}
                        <div className="bg-slate-900 text-white px-8 py-5 flex justify-between items-center shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-uni-teal/20 rounded-xl flex items-center justify-center border border-uni-teal/30">
                                    <ZoomIn className="w-5 h-5 text-uni-teal" />
                                </div>
                                <div>
                                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-uni-teal">Visualización Legajo Seguro</p>
                                    <h3 className="text-base font-black uppercase tracking-tight truncate max-w-[350px] md:max-w-md">{previewTitle}</h3>
                                </div>
                            </div>
                            
                            <button 
                                onClick={closePreview}
                                className="flex items-center gap-2 px-6 py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl transition-all shadow-xl shadow-rose-900/40 text-[11px] font-black uppercase tracking-widest border-b-4 border-rose-800 active:scale-95"
                            >
                                <X className="w-4 h-4" />
                                Cerrar (ESC)
                            </button>
                        </div>

                        {/* Área de Imagen con Scroll Interno si es muy grande */}
                        <div className="flex-1 overflow-auto bg-slate-200 p-6 flex items-center justify-center min-h-[300px]">
                            <img 
                                src={previewUrl} 
                                alt="Vista Documento" 
                                className="max-w-full max-h-[70vh] object-contain shadow-2xl rounded-lg border-8 border-white"
                            />
                        </div>

                        {/* Pie de Página */}
                        <div className="px-10 py-5 bg-white border-t border-slate-100 flex justify-between items-center shrink-0">
                            <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest hidden sm:block">
                                ID Auditoría: {Math.random().toString(36).substr(2, 9).toUpperCase()}
                            </p>
                            <button 
                                onClick={() => {
                                    const a = document.createElement('a');
                                    a.href = previewUrl;
                                    a.download = `${previewTitle}.jpg`;
                                    a.click();
                                }}
                                className="flex items-center gap-3 bg-slate-900 text-white px-10 py-3 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-uni-teal transition-all active:scale-95 shadow-lg"
                            >
                                <Download className="w-4 h-4" />
                                Descargar Copia
                            </button>
                        </div>
                    </div>
                </ModalPortal>
            )}
        </div>
    );
}
